import {CookieOptionsStore} from './CookieOptionsStore';
import {DialogBlockDetector} from './plugins/DialogBlockDetector';
import {BasePlugin} from './plugins/BasePlugin';
import {CookieDialogTrigger} from './plugins/CookieDialogTrigger';
import {PermissionDependantTemplateHandler} from './plugins/PermissionDependantTemplateHandler';
import {DialogModal} from './plugins/DialogModal';

/**
 * Ask user for cookie consent. We use template elements as placeholders for cookie sensitive scripts.
 * The content of these template elements will be loaded if the visitor has given cookie consent.
 */
export class CookieConsent {
    /**
     * Indicates an action was triggered automatically.
     *
     * @type {string}
     */
    static SOURCE_SYSTEM = 'system';

    /**
     * Indicates an action was triggered by a user interaction.
     *
     * @type {string}
     */
    static SOURCE_USER = 'user';

    options = {
        optionsToggleSelector: '[aria-controls="cookie-consent-options"]',
        generalButtonsSelector: '.cookie-consent__buttons.-general',
        agreeValue: 'yes',
        disagreeValue: 'no',
        saveOptionsValue: 'save_options',
        categoriesFieldName: 'categories',
        hideDialog: false,
        isModal: false,
        version: '1',
        plugins: [],

        // Options for default plugins
        dialogBlockDetector: {},
        cookieDialogTrigger: {},
        permissionDependantTemplateHandler: {},
    };

    destroyController = new AbortController();

    constructor(dialog, options = {}, store = new CookieOptionsStore()) {
        this.dialog = dialog;
        this.store = store;

        this.options = {...this.options, ...options, ...this.dialog.dataset};

        // Use the default plugins when no plugin configuration was passed in the options. Passing an empty array
        // is valid configuration and indicates that the consumer does not want plugins.
        if (options.plugins === undefined || options.plugins === null) {
            this.options.plugins = [
                new DialogBlockDetector(this.options.dialogBlockDetector),
                new CookieDialogTrigger(this.options.cookieDialogTrigger),
                new PermissionDependantTemplateHandler(this.options.permissionDependantTemplateHandler),
            ];
        }

        if (this.options.isModal === true) {
            this.options.plugins.push(new DialogModal());
        }

        this.initializePlugins();
        this.initialize();
    }

    initializePlugins() {
        for (const plugin of this.options.plugins) {
            if (!(plugin instanceof BasePlugin)) {
                throw new Error(`${plugin.constructor.name} should extend BasePlugin`);
            }
            plugin.initialize(this);
        }
    }

    /**
     * Get answer and handle accordingly
     * Optionally consent handling can be customized
     */
    initialize() {
        const answerData = this.getSavedAnswer();
        if (!answerData) {
            this.disableDialogForDialogLinks();
        }

        this.attachDialogListeners();
        if (!this.options.hideDialog && !answerData) {
            this.show(CookieConsent.SOURCE_SYSTEM);
        }

        if (answerData && answerData.consent === this.options.agreeValue) {
            this.activateCategories(answerData.categories);
            this.dialog.dispatchEvent(new CustomEvent('consentLoaded', {detail: answerData}));
        }
    }

    destroy() {
        this.destroyController.abort();
        this.destroyPlugins();
    }

    destroyPlugins() {
        for (const plugin of this.options.plugins) {
            plugin.destroy();
        }
    }

    /**
     * Set listener to watch answer
     */
    attachDialogListeners() {
        this.dialog.addEventListener('close', e => this.onDialogClose(e), {signal: this.destroyController.signal});

        this.optionsToggle = this.dialog.querySelector(this.options.optionsToggleSelector);
        this.optionsSection = document.getElementById(this.optionsToggle.getAttribute('aria-controls'));
        this.generalButtons = this.dialog.querySelector(this.options.generalButtonsSelector);

        if (this.optionsToggle && this.optionsSection) {
            this.optionsToggle.addEventListener('click', e => this.onToggleOptionsClick(e), {
                signal: this.destroyController.signal,
            });
        }
    }

    onDialogClose() {
        // Stop if no answer is given
        if (this.dialog.returnValue.length === 0) {
            return;
        }

        let data = {
            version: this.options.version,
            consent: this.dialog.returnValue,
        };
        if (data.consent !== this.options.disagreeValue) {
            data.categories = [];
            let categoryFields = [];
            if (data.consent === this.options.saveOptionsValue) {
                categoryFields = Array.from(this.getCategoryFields()).filter(field => field.checked);
                data.consent = this.options.agreeValue;
            } else if (data.consent === this.options.agreeValue) {
                categoryFields = this.getCategoryFields();
            }

            categoryFields.forEach((categoryField) => {
                data.categories.push(categoryField.value);
            });

            try {
                this.activateCategories(data.categories);
            } catch (e) {
                // In some rare conditions (IE) this can throw an error and then the answer can't be saved
            }
        }

        this.saveAnswer(data);
    }

    onToggleOptionsClick(e) {
        e.preventDefault();
        this.toggleOptions();
    }

    /**
     * Get saved cookie consent
     *
     * @return {Object|null}
     */
    getSavedAnswer() {
        try {
            const data = this.store.getData();
            if (data === null) {
                return null;
            }

            if (data.version === this.options.version) {
                if (typeof data.categories !== 'object') {
                    data.categories = []; // Makes it handy for later use
                }
                return data;
            }
        } catch (e) {
            /* Invalid cookie */
        }

        this.store.removeData();
        return null;
    }

    /**
     * Save consent in a cookie
     * Per answer you can set custom cookie attributes
     *
     * @param {Object} data
     */
    saveAnswer(data) {
        this.store.setData(data);
        this.dialog.dispatchEvent(new CustomEvent('answer', {detail: data}));
    }

    /**
     * Replace the category template elements with its content
     * Send category activate events so other plugins can hook in
     *
     * @param {Array} categories
     */
    activateCategories(categories) {
        categories.forEach(category => {
            this.dialog.dispatchEvent(new CustomEvent('activateCategory', {detail: {category: category}}));
        });
    }

    /**
     * Show dialog
     *
     * @param {String} source
     */
    show(source) {
        this.loadAnswerCategories();

        if (this.options.isModal === true) {
            this.dialog.classList.add('-is-modal');
            this.dialog.showModal();
        } else {
            this.dialog.show();
        }

        this.dialog.dispatchEvent(new CustomEvent('open', {
            detail: {
                source,
            },
        }));
    }

    /**
     * Hide dialog for all links used in the dialog like cookie policy
     */
    disableDialogForDialogLinks() {
        this.dialog.querySelectorAll('a').forEach((link) => {
            if (window.location.href.indexOf(link.href) > -1) {
                this.options.hideDialog = true;
            }
        });
    }

    /**
     * Toggle options
     *
     * @param {Boolean} [open]
     */
    toggleOptions(open) {
        let openValue = open;
        if (typeof open !== 'boolean') {
            openValue = this.optionsToggle.getAttribute('aria-expanded') === 'false';
        }

        this.optionsToggle.setAttribute('aria-expanded', openValue);
        this.optionsSection.toggleAttribute('hidden', !openValue);
        this.generalButtons.toggleAttribute('hidden', openValue);
        this.dialog.dispatchEvent(new CustomEvent('toggleOptions', {detail: {open: openValue}}));
    }

    /**
     * Load saved category choices into the dialog form
     */
    loadAnswerCategories() {
        const answerData = this.getSavedAnswer();
        if (answerData) {
            this.getCategoryFields().forEach((categoryField) => {
                categoryField.checked = answerData.categories.includes(categoryField.value);
            });
        }
    }

    /**
     * Get all category fields
     *
     * @return {NodeList}
     */
    getCategoryFields() {
        return this.dialog.querySelectorAll(
            'input[name="' + this.options.categoriesFieldName + '[]"]'
        );
    }
}
